import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCompanyQuestions } from '../hooks/useCompanyQuestions';
import { useCompanyQuestionMutations } from '../hooks/useCompanyQuestionMutations';
import { QuestionList } from '../components/QuestionList';
import { CreateQuestionDialog } from '../components/CreateQuestionDialog';
import type {
  RefereeQuestion,
  CreateRefereeQuestionRequest,
} from '../types/referee-question.types';

export function CompanyQuestionsSettingsPage(): React.JSX.Element {
  const { companyId } = useParams<{ companyId: string }>();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<RefereeQuestion | null>(null);
  const { questions, hasData, isLoading, error, refetch } = useCompanyQuestions(companyId!);
  const { create, update, remove, isLoading: isMutating } = useCompanyQuestionMutations(companyId!);

  const handleCreate = (data: CreateRefereeQuestionRequest): void => {
    create(data, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        refetch();
      },
    });
  };

  const handleUpdate = (data: { questionId: string; text: string; isRequired: boolean }): void => {
    update(data, {
      onSuccess: () => {
        setIsEditDialogOpen(false);
        setEditingQuestion(null);
        refetch();
      },
    });
  };

  const handleDelete = (questionId: string): void => {
    remove(questionId, {
      onSuccess: () => refetch(),
    });
  };

  const handleEdit = (question: RefereeQuestion): void => {
    setEditingQuestion(question);
    setIsEditDialogOpen(true);
  };

  if (!companyId) {
    return <div className="container py-6">Company ID is required</div>;
  }

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Company reference contact questions</h1>
          <p className="text-muted-foreground">
            Configure company-specific questions for reference contacts
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>Add Question</Button>
      </div>

      <QuestionList
        questions={questions}
        hasData={hasData}
        isLoading={isLoading}
        error={error}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <CreateQuestionDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSubmit={handleCreate}
        isLoading={isMutating}
      />

      <CreateQuestionDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) setEditingQuestion(null);
        }}
        onSubmit={handleCreate}
        onUpdate={handleUpdate}
        editingQuestion={editingQuestion}
        isLoading={isMutating}
      />
    </div>
  );
}
